import { SaleKind, SaleRecord, SalesGateway } from "./SaleRecord.ts";
import {
  HEYLIGHT_FINANCING_KEY_PREFIX,
  SINGLE_PAYMENT_KEY,
  UNKNOWN_KEY,
} from "./salesLabels.ts";

export type SalesFilters = {
  gateways: SalesGateway[];
  paymentMethods: string[];
  installments: string[];
  items: string[];
};

export const NO_SALES_FILTERS: SalesFilters = {
  gateways: [],
  paymentMethods: [],
  installments: [],
  items: [],
};

export const B2B_CONTRACTS_ITEM_KEY = "b2b";

const PAYMENT_METHODS_SHOWN_TOGETHER: Record<string, string> = {
  "stripe.bank_transfer": "stripe.customer_balance",
};

export function itemKeyOf(record: SaleRecord): string | null {
  if (record.item) return record.item.id;
  return record.gateway === SalesGateway.Bonifico &&
    record.kind === SaleKind.Sale
    ? B2B_CONTRACTS_ITEM_KEY
    : null;
}

export function paymentMethodKeyOf(record: SaleRecord): string {
  if (!record.paymentMethod) return UNKNOWN_KEY;
  return (
    PAYMENT_METHODS_SHOWN_TOGETHER[record.paymentMethod] ?? record.paymentMethod
  );
}

export function installmentsKeyOf(record: SaleRecord): string {
  const numberOfInstallments = record.numberOfInstallments
    ? String(record.numberOfInstallments)
    : UNKNOWN_KEY;
  return record.gateway === SalesGateway.HeyLight
    ? `${HEYLIGHT_FINANCING_KEY_PREFIX}${numberOfInstallments}`
    : numberOfInstallments;
}

export function installmentsSeriesKeyOf(
  record: SaleRecord,
  { separatesHeyLightTerms }: { separatesHeyLightTerms: boolean },
): string {
  return record.gateway === SalesGateway.HeyLight && !separatesHeyLightTerms
    ? SINGLE_PAYMENT_KEY
    : installmentsKeyOf(record);
}

export function installmentsKeysOf(record: SaleRecord): string[] {
  return record.gateway === SalesGateway.HeyLight
    ? [SINGLE_PAYMENT_KEY, installmentsKeyOf(record)]
    : [installmentsKeyOf(record)];
}

function installmentsOrderOf(installmentsKey: string): [number, number] {
  const isHeyLightFinancing = installmentsKey.startsWith(
    HEYLIGHT_FINANCING_KEY_PREFIX,
  );
  const numberOfInstallments = Number(
    installmentsKey.replace(HEYLIGHT_FINANCING_KEY_PREFIX, ""),
  );
  return [
    isHeyLightFinancing ? 1 : 0,
    Number.isFinite(numberOfInstallments) ? numberOfInstallments : Infinity,
  ];
}

export function compareInstallmentsKeys(first: string, second: string): number {
  const [firstGroup, firstNumber] = installmentsOrderOf(first);
  const [secondGroup, secondNumber] = installmentsOrderOf(second);
  return firstGroup - secondGroup || firstNumber - secondNumber;
}

function allowsAnyOf<Value>(selected: Value[], value: Value | null): boolean {
  return selected.length === 0 || (value !== null && selected.includes(value));
}

function allowsAnyOfMany<Value>(selected: Value[], values: Value[]): boolean {
  return (
    selected.length === 0 || values.some((value) => selected.includes(value))
  );
}

export function matchesSalesFilters(
  record: SaleRecord,
  filters: SalesFilters,
): boolean {
  return (
    allowsAnyOf(filters.gateways, record.gateway) &&
    allowsAnyOf(filters.paymentMethods, paymentMethodKeyOf(record)) &&
    allowsAnyOfMany(filters.installments, installmentsKeysOf(record)) &&
    allowsAnyOf(filters.items, itemKeyOf(record))
  );
}

export function activeSalesFilterCount(filters: SalesFilters): number {
  return Object.values(filters).filter((selected) => selected.length > 0)
    .length;
}
